import { useState } from 'react';
import { cn } from '@jeraisonne/ui';
import {
  availablePassDurations,
  defaultPassDuration,
  keepPassDuration,
  trialDays,
  type OfferPassDuration,
  type OfferSettings,
} from '@jeraisonne/db/offers';
import { useOfferSettings } from '@/lib/offers/offer-settings';

/**
 * Choix de la durée d'un pass (24 h, 7 jours, 30 jours) sur les paywalls web.
 * Les durées coupées dans l'admin (« Tarifs », migration 0717) disparaissent ;
 * l'abonnement 30 jours annonce l'essai gratuit quand le compte y a droit.
 */

export const DURATION_LABELS: Record<OfferPassDuration, string> = {
  '24h': '24 heures',
  '7d': '7 jours',
  '30d': '30 jours',
};

const GRID_COLS: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-3',
};

/** Durée choisie, toujours parmi celles proposées. */
export function usePassChoice(preferred: OfferPassDuration = '7d') {
  const settings = useOfferSettings();
  const [picked, setPicked] = useState<OfferPassDuration | null>(null);
  const duration = picked
    ? keepPassDuration(settings, picked)
    : defaultPassDuration(settings, preferred);
  return { settings, duration, setDuration: setPicked };
}

/** Sous-titre d'une durée. */
export function passSublabel(duration: OfferPassDuration, settings: OfferSettings): string {
  if (duration === '24h') return 'Accès unique';
  const trial = duration === '30d' ? trialDays(settings) : null;
  return trial ? `${trial} jours gratuits` : 'Abonnement · résiliable';
}

/** Libellé du bouton de paiement. */
export function passCheckoutLabel(
  duration: OfferPassDuration,
  price: string,
  settings: OfferSettings,
): string {
  if (duration === '24h') return `Acheter l’accès 24 h · ${price}`;
  const trial = duration === '30d' ? trialDays(settings) : null;
  if (trial) return `Essayer ${trial} jours gratuits · puis ${price}/mois`;
  return `S’abonner ${DURATION_LABELS[duration]} · ${price}`;
}

/** Phrase d'accroche selon les offres proposées. */
export function passPitch(settings: OfferSettings): string {
  const trial = trialDays(settings);
  if (settings.passes['24h']) {
    return trial
      ? `Pass sans engagement, ou abonnement avec ${trial} jours gratuits, résiliable.`
      : 'Pass sans engagement ou abonnement résiliable.';
  }
  return trial
    ? `${trial} jours gratuits, puis abonnement résiliable à tout moment.`
    : 'Abonnement résiliable à tout moment.';
}

export function PassDurationGrid({
  prices,
  value,
  onChange,
  settings,
}: {
  prices: Record<OfferPassDuration, string>;
  value: OfferPassDuration;
  onChange: (d: OfferPassDuration) => void;
  settings: OfferSettings;
}) {
  const durations = availablePassDurations(settings);
  return (
    <div
      className={cn('grid gap-2', GRID_COLS[durations.length] ?? 'grid-cols-3')}
      role="radiogroup"
      aria-label="Durée"
    >
      {durations.map((d) => {
        const selected = value === d;
        return (
          <button
            key={d}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(d)}
            className={cn(
              'min-h-11 rounded-xl border-2 p-3 text-center transition-all active:scale-[.98]',
              selected ? 'border-primary-400 bg-primary-50' : 'border-ink-200 bg-white',
            )}
          >
            <span
              className={cn(
                'font-display block text-sm font-bold',
                selected ? 'text-primary-700' : 'text-ink-800',
              )}
            >
              {prices[d]}
            </span>
            <span
              className={cn(
                'mt-0.5 block font-sans text-xs',
                selected ? 'text-primary-600' : 'text-ink-500',
              )}
            >
              {DURATION_LABELS[d]}
            </span>
            <span
              className={cn(
                'mt-0.5 block font-sans text-xs leading-tight',
                selected ? 'text-primary-500' : 'text-ink-400',
              )}
            >
              {passSublabel(d, settings)}
            </span>
          </button>
        );
      })}
    </div>
  );
}
